import React, { useEffect } from "react";
import { connectRange } from "react-instantsearch-dom";
import PropTypes from "prop-types";
import Rheostat from "rheostat";
import { debounce } from "lodash";

import { DEFAULT_RATE_RANGE } from "lookup";

import Input from "components/base/Input";

import "./index.css";

const getFiniteNumber = (value, fallback) => {
  const number = Number(value);

  return Number.isFinite(number) ? number : fallback;
};

const handleInputChangeDebounce = debounce(
  ({ value, valueKey }, validate, refine, currentRefinement, min, max) => {
    let payload = { ...currentRefinement, [valueKey]: +value };
    let isValid = !isNaN(+value) && +value >= min && +value <= max;

    if (!isValid) {
      return;
    }

    if (valueKey === "min" && payload.min > payload.max) {
      payload.max = payload.min;
    }

    if (valueKey === "max" && payload.max < payload.min) {
      if (!validate) {
        return;
      }

      value ? (payload.min = payload.max) : (payload.max = payload.min);
    }

    refine({ ...payload });
  },
  500
);

const RangeSlider = ({
  refine,
  currentRefinement = {},
  showInputs,
  displayMin,
  displayMax,
  min,
  max,
  showSlider,
}) => {
  const minBound = getFiniteNumber(min, displayMin);
  const maxBound = getFiniteNumber(max, displayMax);
  const currentMin = getFiniteNumber(currentRefinement.min, minBound);
  const currentMax = getFiniteNumber(currentRefinement.max, maxBound);
  const normalizedRefinement = {
    ...currentRefinement,
    min: currentMin,
    max: currentMax,
  };

  // Cancel queued input refinements so stale typed values cannot re-apply after external resets.
  useEffect(() => {
    return () => {
      handleInputChangeDebounce.cancel();
    };
  }, [currentMin, currentMax, minBound, maxBound]);

  const onChange = ({ values: [newMin, newMax] }) => {
    const payload = { min: newMin, max: newMax };
    const isValid =
      !isNaN(payload.min) &&
      !isNaN(payload.max) &&
      (currentMin !== payload.min || currentMax !== payload.max);

    if (isValid) {
      refine({ ...payload });
    }
  };

  const handleInputChange = ({ value, valueKey }, validate) => {
    handleInputChangeDebounce(
      { value, valueKey },
      validate,
      refine,
      normalizedRefinement,
      minBound,
      maxBound
    );
  };

  return (
    <div>
      {showInputs && (
        <div className="grid grid-cols-2 gap-6">
          <Input
            value={`${currentMin}`}
            valueKey="min"
            label="Min rate"
            placeholder="Type min value"
            onChange={handleInputChange}
            localConversionRate
          />

          <Input
            value={`${currentMax}`}
            valueKey="max"
            label="Max rate"
            placeholder="Type max value"
            onChange={handleInputChange}
            onBlur={(e) => handleInputChange(e, true)}
            localConversionRate
          />
        </div>
      )}

      {showSlider && (
        <>
          <Rheostat
            min={minBound}
            max={maxBound}
            values={[currentMin, currentMax]}
            onChange={onChange}
          />

          <div className="rheostat-values">
            <div>{`$${currentMin}`}</div>
            <div>{`$${currentMax}`}</div>
          </div>
        </>
      )}
    </div>
  );
};

const CustomRangeSlider = connectRange(RangeSlider);

RangeSlider.propTypes = {
  refine: PropTypes.func.isRequired,
  currentRefinement: PropTypes.object,
  showSlider: PropTypes.bool,
  min: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([undefined])]),
  max: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([undefined])]),
};

RangeSlider.defaultProps = {
  min: undefined,
  max: undefined,
  showSlider: false,
  displayMin: DEFAULT_RATE_RANGE.min,
  displayMax: DEFAULT_RATE_RANGE.max,
};

export default CustomRangeSlider;
